import type { MediaSyncProps } from './config';

interface MediaSyncPluginInstance {
  IS_CONCURRENT_UPLOADS_ENABLED?: boolean;
  setApiKey(key: string): MediaSyncPluginInstance;
  setOrganizationId(id: string): MediaSyncPluginInstance;
  setAgentName(name: string): MediaSyncPluginInstance;
  setMaxMediaPerListing(n: number): MediaSyncPluginInstance;
  setAICaptionsEnabled(enabled: boolean): MediaSyncPluginInstance;
  onFetchListings(handler: unknown): MediaSyncPluginInstance;
  onFetchMedia(handler: unknown): MediaSyncPluginInstance;
  onFetchListingDetails(handler: unknown): MediaSyncPluginInstance;
  onImportStart(handler: unknown): MediaSyncPluginInstance;
  onImportComplete(handler: unknown): MediaSyncPluginInstance;
  sync(): Promise<MediaSyncPluginInstance>;
}

type MediaSyncFactory = () => MediaSyncPluginInstance;

const LOG_PREFIX = '[MediaSync]';

export function loadAndInitPlugin(props: MediaSyncProps, debug: boolean): void {
  // The plugin surfaces errors via its own modals (PubSub → VIEW_ERRORS).
  // Suppress the React/Vite dev error overlay to avoid duplicate error UI.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) {
          if (node.tagName === 'VITE-ERROR-OVERLAY') {
            node.remove();
          }
          if (
            node.id === 'webpack-dev-server-client-overlay' ||
            node.id === 'react-error-overlay'
          ) {
            node.remove();
          }
        }
      }
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  if (!props.pluginUrl) {
    console.error(LOG_PREFIX, 'Plugin URL is not configured.');
    return;
  }
  if (!props.apiKey) {
    console.warn(LOG_PREFIX, 'API Key is empty — plugin may not authenticate.');
  }
  if (!props.orgId) {
    console.warn(LOG_PREFIX, 'Org ID is empty — manifest lookup will fail.');
  }

  if (debug) {
    console.log(LOG_PREFIX, 'Config:', {
      pluginUrl: props.pluginUrl,
      orgId: props.orgId,
      agentName: props.agentName,
      manifestId: new URLSearchParams(window.location.search).get('manifestid'),
    });
  }

  // Expose options globally for the plugin script module
  (window as unknown as Record<string, unknown>).mediaSyncOptions = {
    apiKey: props.apiKey,
    orgId: props.orgId,
    agentName: props.agentName,
  };

  const { handlers } = props;

  (window as unknown as Record<string, unknown>).__initMediaSync = async (
    mediaSync: MediaSyncFactory,
  ) => {
    try {
      const ms = mediaSync();

      if (debug) {
        console.log(
          LOG_PREFIX,
          'Plugin instance created, concurrent uploads:',
          ms?.IS_CONCURRENT_UPLOADS_ENABLED,
        );
      }

      if (ms?.IS_CONCURRENT_UPLOADS_ENABLED && handlers.importStart) {
        ms.onImportStart(handlers.importStart);
      }

      ms.setApiKey(props.apiKey)
        .setOrganizationId(props.orgId)
        .setAgentName(props.agentName)
        .setAICaptionsEnabled(true)
        .setMaxMediaPerListing(150)
        .onFetchListings(handlers.fetchListings)
        .onFetchMedia(handlers.uploadMedia)
        .onFetchListingDetails(handlers.fetchListingDetails)
        .onImportComplete(handlers.importComplete);

      await ms.sync();

      if (debug) {
        console.log(LOG_PREFIX, 'Sync started.');
      }
    } catch (err) {
      console.error(LOG_PREFIX, 'Failed to init plugin:', err);
    }
  };

  const script = document.createElement('script');
  script.type = 'module';
  script.crossOrigin = 'anonymous';
  script.textContent = `
    import { mediaSync } from '${props.pluginUrl}/mediaSync.js'
    window.__initMediaSync(mediaSync)
  `;
  document.body.appendChild(script);
}
